import { createI18n } from 'vue-i18n';
import { i18nCommon, loadCommonLocale } from '@r7e/shared';
import { publisherEn } from './locales/publisher.en';

// #############################################################################
// #
// #  Publisher-specific messages
// #
// #  Putting these in separate constants improves readability and
// #  makes it easy to move them to separate files later for better
// #  maintainability as the project grows.
// #
// #############################################################################

// Cache for loaded app-specific locales
const loadedAppLocales = {
  en: publisherEn,
};

// Lazy load app-specific locale function
async function loadAppLocale(locale) {
  // Return cached locale if already loaded
  if (loadedAppLocales[locale]) {
    return loadedAppLocales[locale];
  }

  // Load locale dynamically
  let localeModule;
  switch (locale) {
    case 'fr':
      localeModule = await import('./locales/publisher.fr');
      loadedAppLocales.fr = localeModule.publisherFr;
      return loadedAppLocales.fr;
    case 'de':
      localeModule = await import('./locales/publisher.de');
      loadedAppLocales.de = localeModule.publisherDe;
      return loadedAppLocales.de;
    case 'es':
      localeModule = await import('./locales/publisher.es');
      loadedAppLocales.es = localeModule.publisherEs;
      return loadedAppLocales.es;
    case 'it':
      localeModule = await import('./locales/publisher.it');
      loadedAppLocales.it = localeModule.publisherIt;
      return loadedAppLocales.it;
    case 'ja':
      localeModule = await import('./locales/publisher.ja');
      loadedAppLocales.ja = localeModule.publisherJa;
      return loadedAppLocales.ja;
    case 'zh':
      localeModule = await import('./locales/publisher.zh');
      loadedAppLocales.zh = localeModule.publisherZh;
      return loadedAppLocales.zh;
    case 'en':
      return loadedAppLocales.en;
    default:
      return loadedAppLocales.en; // Fallback to English
  }
}

// Initialize messages with only English loaded
const messages = {
  en: { ...i18nCommon.en || {}, ...publisherEn },
};

// Always initialize with 'en' - saved locale will be loaded in main.js
const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages,
});

// Function to load a locale on demand
export async function loadLocale(locale) {
  // If locale is already loaded, just switch to it
  if (i18n.global.availableLocales.includes(locale)) {
    i18n.global.locale.value = locale;
    return;
  }

  // Load both common and app-specific locales
  const [commonLocale, appLocale] = await Promise.all([
    loadCommonLocale(locale),
    loadAppLocale(locale),
  ]);

  // Merge and set the locale messages
  i18n.global.setLocaleMessage(locale, {
    ...commonLocale,
    ...appLocale,
  });

  // Switch to the new locale
  i18n.global.locale.value = locale;
}

export default i18n;